// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a page made for the chat (T3's html_render) running in place, the way T3 shows it: its scripts
// run, and the frame fits the page. That is only safe where the browser runs a sandboxed frame on a
// thread of its own, or a page stuck in a loop would freeze Konvo for everyone who scrolls past it.
// Measured 2026-11-04 with a busy loop in a sandboxed srcdoc frame: Chrome 148 and Electron 42 kept the
// app running (worst stall 70 ms); with site isolation off, the app froze. Safari, phones and anything
// else keep the still picture (HtmlSnapshot) and open the page in the viewer.

/** The frame's limits. The top only stops a page that grows with its frame. */
export const LIVE_PAGE_MIN_HEIGHT = 80;
export const LIVE_PAGE_MAX_HEIGHT = 20_000;

/** Desktop Chromium: Chrome, Edge and Konvo's desktop app. */
export function pagesCanRunInPlace(): boolean {
	const agent = (navigator as Navigator & {userAgentData?: {brands?: Array<{brand: string}>; mobile?: boolean}})
		.userAgentData;
	if (!agent?.brands?.some(({brand}) => brand === 'Chromium') || agent.mobile) return false;
	return window.matchMedia?.('(hover: hover)').matches ?? false;
}

/**
 * Goes first in the page: tells Konvo the page's height as it changes, and hands over web links (the
 * sandbox lets a page neither open a window nor leave its frame). Konvo trusts neither message: the
 * height is kept to the limits, and a link opens only while the person is using the frame.
 */
export const LIVE_PAGE_SCRIPT = `(function(){
	var last,root=document.documentElement;
	function size(){
		var height=Math.ceil(root.scrollHeight>root.clientHeight?root.scrollHeight:root.getBoundingClientRect().height);
		if(height===last)return;
		last=height;
		parent.postMessage({konvoPageHeight:height},'*');
	}
	var observer=window.ResizeObserver?new ResizeObserver(size):null;
	if(observer)observer.observe(root);
	document.addEventListener('DOMContentLoaded',function(){
		if(observer&&document.body)observer.observe(document.body);
		size();
	});
	window.addEventListener('load',size);
	document.addEventListener('click',function(event){
		var link=event.isTrusted&&event.composedPath().find(function(node){return node&&node.matches&&node.matches('a[href]');});
		if(!link||!/^https?:$/.test(link.protocol))return;
		event.preventDefault();
		parent.postMessage({konvoPageLink:link.href},'*');
	},true);
})();`;

/** What a live page said, once checked; null for anything else. */
export function readLivePageMessage(data: unknown): {height: number} | {link: string} | null {
	if (typeof data !== 'object' || data === null) return null;
	const {konvoPageHeight: height, konvoPageLink: link} = data as {konvoPageHeight?: unknown; konvoPageLink?: unknown};
	if (typeof height === 'number' && Number.isFinite(height))
		return {height: Math.min(LIVE_PAGE_MAX_HEIGHT, Math.max(LIVE_PAGE_MIN_HEIGHT, Math.ceil(height)))};
	if (typeof link === 'string' && /^https?:\/\//i.test(link)) return {link};
	return null;
}
